<template>
    <div class="search_bar">
        <div class="search_input_wrapper">
            <select
                v-model="selectedChain"
                class="select-chain"
                @change="changeFilter"
            >
                <option selected hidden value="Select Chain">
                    Select Chain
                </option>
                <option disabled>Chains:</option>
                <option>AST</option>
                <option>ATH</option>
                <option>ACT</option>
                <option
                    v-for="(option, index) in chainOptions"
                    :id="index"
                    :key="option"
                    :disabled="index === 0"
                >
                    {{ option }}
                </option>
            </select>
            <select
                v-model="selectedFilter"
                class="select-filter"
                placeholder="Select type"
                @change="clearSearchBox"
            >
                <option selected hidden value="Select Filter">
                    Select Filter
                </option>
                <option
                    v-for="(option, index) in filterOptions"
                    :id="index"
                    :key="option"
                >
                    {{ option }}
                </option>
            </select>
            <input
                id="search_input"
                v-model="searchValue"
                type="text"
                :placeholder="placeholder"
                @keyup.enter="oninput"
                @keyup.esc="closeSearch"
                @input="oninput"
                @focus="onfocus"
                @blur="onblur"
            />
            <v-btn
                id="search_icon"
                :loading="isAjax"
                depressed
                @click="selectedChain !== 'ACT' ? oninput() : ''"
            >
                <fa icon="search"></fa>
            </v-btn>
        </div>
        <transition name="fade">
            <div v-if="showResults" class="search_results">
                <div v-if="isAjax" class="no_result">
                    <p>Searching...</p>
                </div>
                <div v-else>
                    <!-- NO RESULTS -->
                    <div v-if="results === null" class="no_result">
                        <p class="icon">
                            <fa icon="snowman"></fa>
                        </p>
                        <p>No Results Found</p>
                    </div>
                    <!-- RESULTS -->
                    <search-result
                        v-for="res in results"
                        :key="getKey(res)"
                        class="search_result"
                        :item="res"
                        @click.native="onSelectResult(res)"
                    ></search-result>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import Vue from 'vue'
import axios from '@/axios'
import SearchResult from '@/components/misc/SearchBar/SearchResult'
import { getEVMTransactionByHash } from '@/services/evmtransactions'
import {
    getEVMBlockByHexHttp,
    getEVMBlockByHashHttp,
} from '@/services/evmblocks'
import {
    DEFAULT_NETWORK_NAME,
    DEFAULT_NETWORK,
    CURRENT_SUBNET,
} from '@/store/modules/network/network'
import { authority } from '@/dcomm'
export default Vue.extend({
    components: {
        SearchResult,
    },
    props: {
        placeholder: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            showResults: false,
            results: [],
            isAjax: false,
            searchValue: '',
            selectedChain: 'Select Chain',
            selectedFilter: 'Select Filter',
            chainOptions: [],
            filterOptions: ['Transaction', 'Address'],
        }
    },
    computed: {
        canSearch() {
            return this.searchValue === '' ? false : true
        },
    },
    created() {
        // assign debounce here (not in methods)
        this.debounceSearch = this.debounce(this.autoSearch, 500)
        this.getBlockchain()
    },
    methods: {
        async getBlockchain() {
            const blockchains = (await authority.getBlockchains()).map((b) => {
                if (this.checkSubnetUrl(b.name.toLowerCase())) {
                    this.chainOptions.push('Subnets:')
                    this.chainOptions.push(b.name)
                }
            })
            this.chainOptions = new Set(this.chainOptions)
        },
        onfocus() {
            // display previous results, if any
            if (this.searchValue.length > 0) {
                this.showResults = true
            }
        },
        onblur() {
            this.showResults = false
            this.results = []
        },
        checkSubnetUrl(subnet) {
            const getSubnet = DEFAULT_NETWORK.subnets.map((x) => {
                const sub = CURRENT_SUBNET !== null ? CURRENT_SUBNET : ''
                if (subnet in x) {
                    return true
                }
            })
            if (getSubnet[0]) {
                return getSubnet[0]
            } else {
                return false
            }
        },
        // Hitting [enter] takes you to the search page
        search() {
            if (!this.canSearch) return
            this.$emit(
                'search',
                this.searchValue,
                this.selectedChain,
                this.selectedFilter
            )
            this.closeSearch()
        },
        clearSearchBox() {
            this.searchValue = ''
        },
        changeFilter() {
            this.selectedFilter = 'Select Filter'
            this.searchValue = ''
            if (this.selectedChain === 'ACT') {
                this.filterOptions = [
                    'Transaction(ACT)',
                    'Transaction(Cross-chain)',
                    'Block Hash',
                    'Block Height',
                    'Address',
                ]
                this.$router.push(
                    `${window.location.pathname}?network=${DEFAULT_NETWORK_NAME}`
                )
                this.$store.dispatch('Network/updateSubnet')
            } else if (
                this.selectedChain === 'AST' ||
                this.selectedChain === 'ATH'
            ) {
                this.filterOptions = ['Transaction', 'Address']
                this.$router.push(
                    `${window.location.pathname}?network=${DEFAULT_NETWORK_NAME}`
                )
                this.$store.dispatch('Network/updateSubnet')
            } else {
                this.filterOptions = [
                    'Transaction Hash',
                    'Block Hash',
                    'Block Height',
                    'Address',
                ]
                this.$router.push(
                    `${window.location.pathname}?network=${DEFAULT_NETWORK_NAME}&subnet=${this.selectedChain}`
                )
                this.$store.dispatch('Network/updateSubnet')
            }
        },
        oninput() {
            this.$emit('input', this.searchValue)
            this.debounceSearch()
        },
        autoSearch() {
            let query = this.searchValue
            const SEARCH_LIM = 10

            if (query === '') {
                this.showResults = false
                return
            }

            if (this.selectedFilter === 'Block Height') {
                const isnum = /^\d+$/.test(this.searchValue)
                if (!isnum) {
                    this.results = null
                    this.$store.dispatch('Notifications/add', {
                        title: 'Invalid input',
                        message: 'Number is required.',
                        type: 'warning',
                    })
                    this.results = null
                    return
                }
            }

            const split = query.split('-')
            query = split[split.length - 1]

            this.isAjax = true
            this.showResults = true
            // TODO: support service for multiple chains
            if (this.selectedChain === 'AST' || this.selectedChain === 'ATH') {
                axios
                    .get(`/ast/search?query=${query}&limit=${SEARCH_LIM}`)
                    .then((res) => {
                        const data = res.data
                        this.results =
                            data.results.length > 0 ? data.results : null
                        this.isAjax = false
                    })
            } else {
                // TODO: HERE WE WILL GET ADD THE API FOR EVM TX WITH CONDITIONS
                if (this.selectedFilter === 'Transaction(ACT)') {
                    getEVMTransactionByHash({ hash: query }).then((res) => {
                        const response = res
                        this.isAjax = false
                        if (response && response.blockHash) {
                            this.$router.push({
                                path: `/evmtx/${query}`,
                                query: { network: DEFAULT_NETWORK_NAME },
                            })
                        } else {
                            this.results = null
                        }
                    })
                } else if (this.selectedFilter === 'Block Hash') {
                    getEVMBlockByHashHttp(query, this.isACT).then((res) => {
                        const response = res
                        this.isAjax = false
                        if (response.data.result) {
                            this.selectedChain === 'ACT'
                                ? this.$router.push({
                                      path: `/evmblock/${query}`,
                                      query: { network: DEFAULT_NETWORK_NAME },
                                  })
                                : this.$router.push({
                                      path: `/evmblock/${query}`,
                                      query: {
                                          network: DEFAULT_NETWORK_NAME,
                                          subnet: this.selectedChain,
                                      },
                                  })
                        } else {
                            this.results = null
                        }
                    })
                } else if (this.selectedFilter === 'Transaction Hash') {
                    getEVMTransactionByHash({ hash: query }).then((res) => {
                        const response = res
                        this.isAjax = false
                        if (response && response.blockHash) {
                            this.selectedChain === 'ACT'
                                ? this.$router.push({
                                      path: `/evmtx/${query}`,
                                      query: { network: DEFAULT_NETWORK_NAME },
                                  })
                                : this.$router.push({
                                      path: `/evmtx/${query}`,
                                      query: {
                                          network: DEFAULT_NETWORK_NAME,
                                          subnet: this.selectedChain,
                                      },
                                  })
                        } else {
                            this.results = null
                        }
                    })
                } else if (this.selectedFilter === 'Block Height') {
                    getEVMBlockByHexHttp(query).then((res) => {
                        const response = res
                        this.isAjax = false
                        if (response.data.result) {
                            this.selectedChain === 'ACT'
                                ? this.$router.push({
                                      path: `/evmblock/${query}`,
                                      query: { network: DEFAULT_NETWORK_NAME },
                                  })
                                : this.$router.push({
                                      path: `/evmblock/${query}`,
                                      query: {
                                          network: DEFAULT_NETWORK_NAME,
                                          subnet: this.selectedChain,
                                      },
                                  })
                            return
                        } else {
                            this.results = null
                            this.isAjax = false
                            return
                        }
                    })
                } else if (this.selectedFilter === 'Address') {
                    if (!query.startsWith('0x') || query.length !== 42) {
                        // this.$router.replace('/404')
                        this.results = null
                        this.isAjax = false
                        return
                    }
                    this.selectedChain === 'ACT'
                        ? this.$router.push({
                              path: `/evmaddress/${query}`,
                              query: { network: DEFAULT_NETWORK_NAME },
                          })
                        : this.$router.push({
                              path: `/evmaddress/${query}`,
                              query: {
                                  network: DEFAULT_NETWORK_NAME,
                                  subnet: this.selectedChain,
                              },
                          })
                    this.isAjax = false
                } else if (this.selectedFilter === 'Transaction(Cross-chain)') {
                    axios
                        .get(`/ast/search?query=${query}&limit=${SEARCH_LIM}`)
                        .then((res) => {
                            const data = res.data
                            if (data.count !== 0) {
                                this.results = data.results
                                this.isAjax = false
                                return
                            } else {
                                this.results = null
                                this.isAjax = false
                                return
                            }
                        })
                }
            }
        },
        debounce(func, wait, immediate) {
            let timeout
            return function executedFunction(...theArgs) {
                const context = this
                const args = theArgs
                const later = function () {
                    timeout = null
                    if (!immediate) func.apply(context, args)
                }
                const callNow = immediate && !timeout
                clearTimeout(timeout)
                timeout = setTimeout(later, wait)
                if (callNow) func.apply(context, args)
            }
        },
        getKey(item) {
            let res = item.type
            if (item.type === 'address') {
                res += item.data.address
            } else if (item.data.evmtx) {
                res += item.data.hash
            } else {
                res += item.data.id
            }
            return res
        },
        onSelectResult() {
            this.showResults = false
            this.searchValue = ''
        },
        closeSearch() {
            this.showResults = false
            this.searchValue = ''
            this.results = []
        },
    },
})
</script>

<style scoped lang="scss">
.search_input_wrapper {
    width: 100%;
    padding: 5px;
    display: flex;
    box-sizing: border-box;
    border-radius: 50px;
    gap: 2px;
}

.search_bar {
    display: flex;
    background-color: transparent;
    position: relative;
    select {
        background: var(--bg-explorer-light);
        border: none;
        font-style: normal;
        font-weight: 400;
        font-size: 14px;
        padding: 5px 10px;
        color: var(--font);
        option {
            background-color: var(--background-color) !important;
        }
        @media screen and (min-width: 960px) and (max-width: 1215px) {
            width: 10vw;
        }
    }
}

#search_input {
    background-color: transparent;
    padding: 0px 13px;
    flex-grow: 1;
    outline: none;
    font-size: 15px;
    color: var(--font) !important;
    border: unset;
    border-bottom: 1px solid var(--font-light);
    width: 20vw;
    @media screen and (min-width: 960px) and (max-width: 1215px) {
        width: 10vw;
    }
    &::placeholder {
        color: var(--font-light) !important;
    }
}

.v-btn {
    border: none;
    border-radius: 0 10px 10px 0;
    cursor: pointer;
    font-size: 12px;
    min-width: 40px !important;
    min-height: 40px !important;
    padding: 0 !important;
    color: var(--font) !important;
    background-color: var(--bg-explorer-light) !important;
}

.search_results {
    position: absolute;
    width: 100%;
    box-sizing: border-box;
    border-radius: 0 0 2px 2px;
    top: calc(100% + 1px);
    left: 0px;
    font-size: 12px;
    background-color: var(--bg-explorer);
    backdrop-filter: blur(25px);
}

.no_result {
    padding: 15px 30px;
    display: flex;
    align-items: center;

    .icon {
        font-size: 22px;
        margin-right: 15px;
        opacity: 0.4;
    }
}
#search_icon {
    &:disabled {
        background-color: rgba(0, 0, 0, 0.1) !important;
        color: white !important;
    }
}
.select-filter {
    min-width: 160px;
    @media screen and (min-width: 960px) and (max-width: 1215px) {
        min-width: unset;
    }
}
.select-chain {
    border-radius: 10px 0px 0px 10px;
}

.search_result {
    border-bottom: 1px solid var(--font-light);
    cursor: pointer;

    &:last-child {
        border-bottom: none;
    }
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
    opacity: 0;
}

@include smOnly {
    .search_results {
        position: fixed;
    }
}
</style>
